"use client";

/**
 * 首页首屏 Hero：背景视频 + 主文案在加载后渐入（非滚动触发，利于 SEO）；
 * 通过 HOME_HERO_REVEAL_EVENT 通知顶栏同步显现。
 */
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useRef } from "react";
import { FreeDigitalReportHeroButton } from "@/components/shared/DigitalReportLeadProvider";
import {
  HOME_HERO_REVEAL_EVENT,
  HOME_HERO_REVEAL_SCROLL_PX,
  HOME_HERO_LOAD_REVEAL_DELAY_S,
} from "@/lib/home-hero-intro";

function dispatchHomeHeroReveal() {
  window.dispatchEvent(new CustomEvent(HOME_HERO_REVEAL_EVENT));
}

export function HeroSection() {
  const scope = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const revealedRef = useRef(false);

  useGSAP(
    () => {
      const overlay = overlayRef.current;
      const main = mainRef.current;
      if (!overlay || !main) return;

      gsap.set(overlay, { opacity: 0 });
      gsap.set(main, { autoAlpha: 0 });

      const kids = main.querySelectorAll<HTMLElement>("[data-hero-reveal]");

      const playReveal = () => {
        const tl = gsap.timeline();
        tl.to(overlay, {
          opacity: 0.7,
          duration: 0.48,
          ease: "power2.inOut",
        })
          .to(
            main,
            { autoAlpha: 1, duration: 0.22, ease: "power2.out" },
            "-=0.22"
          )
          .from(
            kids,
            {
              autoAlpha: 0,
              y: 18,
              stagger: 0.05,
              duration: 0.4,
              ease: "power3.out",
            },
            "-=0.1"
          );
      };

      const snapReveal = () => {
        gsap.set(overlay, { opacity: 0.45 });
        gsap.set(main, { autoAlpha: 1 });
        gsap.set(kids, { autoAlpha: 1, y: 0 });
      };

      const commitReveal = (mode: "snap" | "play") => {
        if (revealedRef.current) return;
        revealedRef.current = true;
        if (mode === "snap") {
          snapReveal();
        } else {
          playReveal();
        }
        dispatchHomeHeroReveal();
      };

      if (window.scrollY > HOME_HERO_REVEAL_SCROLL_PX) {
        commitReveal("snap");
        return;
      }

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        commitReveal("snap");
        return;
      }

      const delayedReveal = gsap.delayedCall(
        HOME_HERO_LOAD_REVEAL_DELAY_S,
        () => commitReveal("play")
      );

      return () => {
        delayedReveal.kill();
      };
    },
    { scope }
  );

  return (
    <section
      ref={scope}
      className="relative flex min-h-dvh flex-col overflow-x-hidden font-sans text-white"
    >
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        <div className="absolute inset-0 bg-[#0a0a0a]" />
        <div className="absolute inset-0 overflow-hidden">
          <video
            className="absolute inset-0 h-full w-full origin-center object-contain object-center scale-[2.5] motion-reduce:scale-100 md:scale-100 md:object-cover"
            autoPlay
            muted
            playsInline
            preload="auto"
          >
            <source src="/images/main_comp_super_fast.mp4" type="video/mp4" />
          </video>
        </div>

        {/* 遮罩 mask for mobile */}
        <div className="absolute inset-x-0 top-20 z-50 h-[50%] bg-linear-to-b from-black to-transparent md:hidden" />
        <div className="absolute inset-x-0 bottom-20 z-50 h-[50%] bg-linear-to-t from-black to-transparent md:hidden" />

        <div
          ref={overlayRef}
          className="absolute inset-0 bg-black opacity-0"
        />
      </div>

      <div className="relative z-10 flex min-h-dvh flex-col overflow-hidden pt-16 md:pt-20">
        <div
          ref={mainRef}
          className="invisible flex flex-1 items-end py-12 opacity-0 md:items-center md:py-16"
        >
          <div className="site-content">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-20">
              <div className="lg:col-span-7 max-w-[750px]">
                <h1
                  data-hero-reveal
                  className="text-sm font-medium tracking-tight text-white/85 md:text-base"
                >
                  Web design &amp; digital marketing
                </h1>
                <h2
                  data-hero-reveal
                  className="mt-4 text-7xl tracking-tight text-white sm:mt-5 sm:text-5xl lg:text-8xl xl:text-[150px]"
                >
                  Normality is where brands{" "}
                  <span className="text-[#ff5c00]">die.</span>
                </h2>
                <hr
                  data-hero-reveal
                  className="mt-8 max-w-xl border-0 border-t border-white/90"
                />
                <p
                  data-hero-reveal
                  className="mt-6 max-w-md text-sm leading-relaxed text-white/95 md:text-lg"
                >
                  Find out how your brand stacks up in the digital landscape
                  with our free digital report
                </p>
                <span data-hero-reveal className="mt-10 block w-max md:mt-12">
                  <FreeDigitalReportHeroButton />
                </span>
              </div>

              <div className="lg:col-span-5 flex flex-col justify-center">
                <p
                  data-hero-reveal
                  className="text-base font-medium leading-snug text-[#ff5c00] md:text-3xl"
                >
                  <span className="mr-1 hidden text-4xl text-[#ff5c00] md:block">*</span>
                  We&apos;re a digital agency that designs websites, apps and
                  digital journeys that deliver growth &amp; independence
                </p>
                <p
                  data-hero-reveal
                  className="mt-6 text-sm leading-normal text-white/90 md:text-lg"
                >
                  We create ideas from strategy, fuelled by creativity, and
                  measured by results. From bold concepts and distinctive brand
                  journeys to high-performing website development and digital
                  experiences designed to convert your audience into customers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
